import { Dispatch, SetStateAction, useCallback, useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { CreateOrderDialogFlow, CreateOrderDialogMode, CreateOrderDialogType, OnBehalfOfUser } from "./createOrderTypes"
import { fetchOrderDetails, OrderDetailsResponse } from "../../../modules/nonAdminUser/reports/home/meetingServiceApi"
import { OutlookBookingSuccessArgs } from "../../../modules/order/pages/outlookCreateOrderTypes"
import { isCalendarOrganizer } from "../../../modules/order/pages/helpers"
import { InvalidUserPermissionsDialog } from "./orderOnBehalf/InvalidUserPermissionsDialog"
import { UserWeekMenu } from "../../../modules/nonAdminUser/reports/home/weekMenuApi"
import { CreateOrderDialogContent } from "./mainForm/CreateOrderDialogContent"
import { CreateOrderDialogWrap } from "./components/CreateOrderDialogWrap"
import { patchedOrderDetails } from "./mainForm/patchedOrderDetails"
import { OnBehalfCompletedDialog } from "./hooks/useCreateOrderUser"
import { UserType } from "../../../modules/login/loginTypes"
import { getOrderPerson } from "../../utils/getOrderPerson"
import { useApiQuery } from "../../../core/api/useApiQuery"
import { OrderContextState } from "./mainForm/helpers"
import { ConfigQuery } from "./createOrderApi"
import { CreateOrderType } from "./helpers"

export const transformOrderDetails = (data: OrderDetailsResponse) => (data?.orders.length ? data?.orders[0] : undefined)

type CreateOrderDialogControllerProps = Readonly<{
    open: boolean
    handleClose: () => void
    activeOrderTab?: CreateOrderType
    dialogType: CreateOrderDialogType
    orderId?: number
    mode: CreateOrderDialogMode
    flowConfig?: CreateOrderDialogFlow
    onBehalfOf?: OnBehalfOfUser
    isOnBehalfCompleted: OnBehalfCompletedDialog
    setIsOnBehalfCompleted: Dispatch<SetStateAction<OnBehalfCompletedDialog>>
    handleOutlookBookingCompleted?: (args: OutlookBookingSuccessArgs) => void
}>

export function CreateOrderDialogController({
    open,
    handleClose,
    activeOrderTab,
    dialogType,
    orderId,
    mode,
    flowConfig,
    onBehalfOf,
    isOnBehalfCompleted,
    setIsOnBehalfCompleted,
    handleOutlookBookingCompleted,
}: CreateOrderDialogControllerProps) {
    const queryClient = useQueryClient()
    const [showInvalidUserPermissions, setShowInvalidUserPermissions] = useState(false)
    const [orderContext, setOrderContext] = useState<OrderContextState>({ mode, orderId })
    // Bumped only when the user picks a different order from the timeline. Used as a remount key
    // for the form subtree so Formik and the resource booking draft refs are rebuilt from the new
    // order instead of surviving with the previous one's values (which happens whenever the new
    // order's details are already cached and the loading gate below never hides the content).
    // Deliberately NOT keyed on orderContext.orderId — that also changes right after a successful
    // create, which would unmount the success dialog.
    const [switchEpoch, setSwitchEpoch] = useState(0)

    const switchToOrder = useCallback(
        (nextOrderId: number) => {
            if (!Number.isFinite(nextOrderId) || nextOrderId <= 0) return

            // Same reason as the clear on dialog open in CreateOrderDialogHost: the new order re-runs
            // the form reinitialize/sync cycle, and a synchronous extra-services cache hit collides
            // with it. Must remove (not invalidate) — invalidate keeps serving the data synchronously.
            queryClient.removeQueries({ queryKey: [ConfigQuery.EXTRA_SERVICES_FOR_USER] })

            setOrderContext({ mode: CreateOrderDialogMode.EDIT, orderId: nextOrderId })
            setSwitchEpoch((epoch) => epoch + 1)
        },
        [queryClient],
    )

    // Must read orderContext (not the mode prop): switching from a create session to an existing
    // order only updates the context, and the prop would keep the query disabled forever.
    const queryEnabled = Boolean(orderContext?.orderId) && orderContext.mode !== CreateOrderDialogMode.CREATE

    const { data: baseOrderDetails, isFetching } = useApiQuery(fetchOrderDetails, {
        queryName: UserWeekMenu.USER_MEETING_SERVICE_DETAILS,
        dependencies: {
            orderId: orderContext?.orderId!,
            flow: flowConfig?.vstoFlowParam,
        },
        enabled: queryEnabled,
        select: transformOrderDetails,
    })

    const handleInvalidUserPermissions = () => {
        // orderContext.mode, not the prop: after switching to an existing order from the timeline the
        // session is an edit even when the dialog was opened in create mode, and "select another user"
        // must stay hidden for it. CreateOrderDialogContent gates availableModules on the same value.
        if (orderContext.mode === CreateOrderDialogMode.DUPLICATE || orderContext.mode === CreateOrderDialogMode.EDIT)
            return
        setShowInvalidUserPermissions(false)
        setIsOnBehalfCompleted((prev) => {
            return { ...prev, show: true }
        })
    }

    const isLoadingOrderDetails = queryEnabled && isFetching && !baseOrderDetails

    const orderDetails = !isLoadingOrderDetails
        ? patchedOrderDetails({
              flowConfigOrder: flowConfig?.order,
              orderDetails: baseOrderDetails,
          })
        : undefined
    const { person: orderPerson, creator, organizer } = getOrderPerson(orderDetails?.organizers)
    // ORGANIZER is the on-behalf-of user (the order's customer); CREATOR is the logged-in user
    // who created the order (an admin for on-behalf orders). User details / module gating must use
    // the ORGANIZER, falling back to CREATOR when the ORGANIZER is missing or has an unusable id
    // (it can be 0) — never the admin, whose /users/{id} read 403s. id and name come from the same
    // resolved person so they stay consistent.
    // This resolution is for admin types only: their own id is 0 and has no /users/{id} record.
    // A regular user always acts as themselves — the order's ORGANIZER can be a different account
    // (e.g. resolved from the meeting organizer's email rather than the CREATOR record), which they
    // may read no more than they may save to, so both /users/{id} and the update call would 403.
    // A shared calendar is the exception to "ORGANIZER is the customer": there the entry is a mailbox,
    // and the backend resolves its address to that mailbox's owner, so it arrives with a usable id and
    // would otherwise be adopted as the order's user — booking config and user details would then be
    // read for the calendar's owner instead of the customer.
    const isRegularUser = isOnBehalfCompleted.userType === UserType.USER
    const organizerPerson =
        organizer?.id &&
        !isCalendarOrganizer(organizer, {
            customerId: orderDetails?.customer?.id,
            organizers: orderDetails?.organizers,
        })
            ? organizer
            : undefined
    const organizerUser = isRegularUser ? undefined : organizerPerson
    const orderUser = isRegularUser ? undefined : (organizerUser ?? (creator?.id ? creator : undefined))
    const onBehalfDisplayName =
        orderDetails?.customer?.displayName ?? orderPerson?.name ?? isOnBehalfCompleted.displayName
    // API doesn't return onBehalfOf: recover it from ORGANIZER, only on own non-Outlook orders
    const savedOnBehalfOf: OnBehalfOfUser | undefined =
        isRegularUser &&
        !flowConfig &&
        (orderContext.mode === CreateOrderDialogMode.EDIT || orderContext.mode === CreateOrderDialogMode.DUPLICATE) &&
        !orderDetails?.meetingDetails?.calendarAppointmentId &&
        creator?.id === isOnBehalfCompleted.userId &&
        organizerPerson?.id &&
        organizerPerson.id !== isOnBehalfCompleted.userId
            ? {
                  userId: organizerPerson.id,
                  displayName: organizerPerson.name ?? orderDetails?.customer?.displayName ?? "",
              }
            : undefined
    const userOnBehalfOf =
        isRegularUser && orderContext.mode === CreateOrderDialogMode.CREATE ? onBehalfOf : savedOnBehalfOf

    return (
        <>
            <CreateOrderDialogWrap
                onBehalfOf={isOnBehalfCompleted.userType != UserType.USER}
                open={open}
                onClose={handleClose}
                flowConfig={flowConfig}
                onAccessDenied={() => setShowInvalidUserPermissions(true)}
            >
                {!isLoadingOrderDetails && (
                    <CreateOrderDialogContent
                        key={switchEpoch}
                        flowConfig={flowConfig}
                        handleClose={handleClose}
                        user={{
                            userId: orderUser?.id ?? isOnBehalfCompleted.userId,
                            displayName: orderUser?.name ?? isOnBehalfCompleted.displayName,
                            companyId: isOnBehalfCompleted.companyId,
                            userType: isOnBehalfCompleted.userType,
                            onBehalfOf: userOnBehalfOf,
                        }}
                        activeOrderTab={activeOrderTab}
                        dialogType={dialogType}
                        handleInvalidUserPermissions={() => setShowInvalidUserPermissions(true)}
                        orderContext={orderContext}
                        setOrderContext={setOrderContext}
                        switchToOrder={switchToOrder}
                        orderDetails={orderDetails}
                        handleOutlookBookingCompleted={handleOutlookBookingCompleted}
                    />
                )}
            </CreateOrderDialogWrap>
            {showInvalidUserPermissions && (
                <InvalidUserPermissionsDialog
                    isOnBehalfCompleted={isOnBehalfCompleted}
                    activeOrderTab={activeOrderTab}
                    mode={orderContext.mode}
                    onBehalfDisplayName={onBehalfDisplayName}
                    handleClose={handleClose}
                    handleShowOrderOnBehalfDialog={handleInvalidUserPermissions}
                />
            )}
        </>
    )
}
